import { z } from 'zod';
import { zStringRequired } from '../../z';
import { frequencyEntrySchema } from '../patient-consent';
// Tolerates legacy saves where frequency was undefined, null, or a free-text string.
// Any non-array value is coerced to [] so the field-array UI always gets a valid array.
// Valid arrays pass through unchanged so item-level validation still runs.
export const frequencyArraySchema = z.preprocess((val) => (Array.isArray(val) ? val : []), z.array(frequencyEntrySchema));
export const vitalBothSchema = z
    .object({ enabled: z.boolean().optional(), notifyBelow: z.string().optional(), notifyAbove: z.string().optional() })
    .optional();
export const vitalHighOnlySchema = z.object({ enabled: z.boolean().optional(), notifyAbove: z.string().optional() }).optional();
export const vitalLowOnlySchema = z.object({ enabled: z.boolean().optional(), notifyBelow: z.string().optional() }).optional();
// Shared between v1 and v2 GeneralOrders — the vitals parameter shapes are identical across versions.
export function vitalsThresholdRefinement(data) {
    return !Object.values(data.parameters ?? {}).some((v) => {
        if (!v?.enabled) {
            return false;
        }
        const vv = v;
        const hasBelow = 'notifyBelow' in vv;
        const hasAbove = 'notifyAbove' in vv;
        const belowEmpty = hasBelow && !vv.notifyBelow;
        const aboveEmpty = hasAbove && !vv.notifyAbove;
        return belowEmpty || aboveEmpty;
    });
}
export const VITALS_THRESHOLD_REFINEMENT_ISSUE = {
    message: 'Threshold values are required when enabled',
    path: ['parameters'],
};
export const generalOrdersShape = z.object({
    order: z.string().optional(),
    parameters: z
        .object({
        heartRate: vitalBothSchema,
        temperature: vitalBothSchema,
        respirations: vitalBothSchema,
        painLevel: vitalHighOnlySchema,
        o2Saturation: vitalLowOnlySchema,
        bpSystolic: vitalBothSchema,
        bpDiastolic: vitalBothSchema,
        weightChange: vitalHighOnlySchema,
        bloodSugar: vitalBothSchema,
    })
        .optional(),
    anticipatedDischargeSetting: z.string().nullish(),
    dischargePlanningDate: z.string().nullish(),
    barrierToDischarge: z.string().nullish(),
});
export const planOfCare485ValidationSchema = z.object({
    id: z.string(),
    data: z.object({
        General: z.object({
            primaryPhysician: zStringRequired(),
            primaryCaregiver: z.string().nullish(),
        }),
        PlanOfCare: z.boolean().refine((value) => !!value, { message: 'section has errors' }),
        Medications: z.boolean(),
        AbilityLimitation: z.object({
            rehabPotential: z.object({
                A: z.string().nullish(),
                B: z.string().nullish(),
                C: z.string().nullish(),
                D: z.string().nullish(),
            }),
            prognosis: z.string().nullish(),
            functionalLimitation: z.array(z.string()).optional(),
            functionalLimitationOther: z.string().nullish(),
            activitiesPermitted: z.array(z.string()).optional(),
            activitiesPermittedOther: z.string().nullish(),
        }),
        Hospitalization: z.object({
            risks: z.array(z.string()).optional(),
            risksOther: z.string().nullish(),
            necessaryInterventions: z.array(z.string()).optional(),
        }),
        MentalStatus: z
            .object({
            wnl: z.boolean().optional(),
            alertness: z.string().optional(),
            orientedToPerson: z.string().optional(),
            orientedToPlace: z.string().optional(),
            orientedToPurpose: z.string().optional(),
            orientedToTime: z.string().optional(),
            status: z.array(z.string()).optional(),
        })
            .refine((data) => data.wnl ||
            data.alertness ||
            data.orientedToPerson ||
            data.orientedToPlace ||
            data.orientedToPurpose ||
            data.orientedToTime ||
            data.status?.length, {
            message: 'Mark as within normal limits if none applies',
            path: [],
        }),
        Supplies: z.boolean(),
        Safety: z.object({
            safetyMeasures: z.array(z.string()).optional(),
            ambulateLocation: z.string().nullish(),
            animalDescription: z.string().nullish(),
        }),
        NutritionAllergies: z.object({
            nutritionalRequirements: z.array(z.string()).optional(),
            nutritionalRequirementsOther: z.string().nullish(),
            tubeFeeding: z.boolean().nullish(),
            tubeFeedingFreq: z.string().nullish(),
            allergies: z.array(z.object({ allergy: z.string(), severity: z.string() })),
        }),
        DisciplineOrders: z.object({
            sn: z
                .object({
                service: z.string().optional(),
                frequency: frequencyArraySchema,
                prnVisits: z.string().optional(),
                tasks: z.array(z.string()).optional(),
                medicationName: z.string().optional(),
                other: z.string().optional(),
            })
                .refine((data) => data.service || data.frequency.length || data.prnVisits || data.tasks?.length, {
                message: 'Please fill in SN service information',
                path: [],
            }),
            ha: z
                .object({
                service: z.string().optional(),
                frequency: frequencyArraySchema,
                tasks: z.array(z.string()).optional(),
                reportChangesOther: z.string().optional(),
            })
                .refine((data) => data.service || data.frequency.length || data.tasks?.length, {
                message: 'Please fill in HHA service information',
                path: [],
            }),
            msw: z
                .object({
                service: z.string().optional(),
                frequency: frequencyArraySchema,
            })
                .refine((data) => data.service || data.frequency.length, {
                message: 'Please fill in MSW service information',
                path: [],
            }),
            pt: z
                .object({
                service: z.string().optional(),
                frequency: frequencyArraySchema,
            })
                .refine((data) => data.service || data.frequency.length, {
                message: 'Please fill in PT service information',
                path: [],
            }),
            ot: z
                .object({
                service: z.string().optional(),
                frequency: frequencyArraySchema,
            })
                .refine((data) => data.service || data.frequency.length, {
                message: 'Please fill in OT service information',
                path: [],
            }),
            slp: z
                .object({
                service: z.string().optional(),
                frequency: frequencyArraySchema,
                tasks: z.array(z.string()).optional(),
            })
                .refine((data) => data.service || data.frequency.length || data.tasks?.length, {
                message: 'Please fill in SLP service information',
                path: [],
            }),
            advanceDirectives: z.object({
                livingWill: z.string().min(1, 'required'),
                dnr: z.string().min(1, 'required'),
                surrogate: z.string().min(1, 'required'),
                copyObtained: z.string().min(1, 'required'),
            }),
            dmeOrders: z.string().nullish(),
            oxygenOrders: z.string().nullish(),
            patientCopyProvided: z.boolean().nullish(),
        }),
        GeneralOrders: generalOrdersShape.refine(vitalsThresholdRefinement, VITALS_THRESHOLD_REFINEMENT_ISSUE),
    }),
    signedUserId: zStringRequired(),
    signedUserDate: zStringRequired(),
    // nullish: DB/client may send null; dismissedSuggestions optional so meta: {} is valid.
    meta: z
        .object({
        dismissedSuggestions: z
            .object({
            functionalLimitation: z.array(z.string()).optional(),
            prognosis: z.boolean().optional(),
            rehabPotentialA: z.boolean().optional(),
            rehabPotentialB: z.boolean().optional(),
            rehabPotentialD: z.boolean().optional(),
            adLivingWill: z.boolean().optional(),
            adDnr: z.boolean().optional(),
            adSurrogate: z.boolean().optional(),
            adCopyObtained: z.boolean().optional(),
        })
            .optional(),
    })
        .nullish(),
});
